"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { useEmailAgents } from "@/hooks/agents/email";
import { useTranslations } from "next-intl"



interface DangerZoneSettingsProps {
  agentId: string;
  onDelete: () => void;
}

export function DangerZoneSettings({ agentId, onDelete }: DangerZoneSettingsProps) {

  const tUi = useTranslations("Email.AgentsEmailTabsDangerZoneSettings")

  const { agents, deleteAgent } = useEmailAgents();
  const agent = agents.find((a) => a.id === agentId);

  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (confirmText !== agent?.name) {
      setError(tUi("pleaseTypeTheAgentNameToConfirmDeletion"));
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await deleteAgent(agentId);
      onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToDeleteAgent"));
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <span><span key={tUi("dangerZone")}>{tUi("dangerZone")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("irreversibleAndDestructiveActions")}>{tUi("irreversibleAndDestructiveActions")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2"><span key={tUi("deleteThisAgent")}>{tUi("deleteThisAgent")}</span></h4>
            <p className="text-sm text-red-700 mb-4">
              <span key={tUi("onceYouDeleteAnAgentThereIsNo")}>{tUi("onceYouDeleteAnAgentThereIsNo")}</span></p>

            <div className="space-y-3">
              <div>
                <Label htmlFor="confirm-delete" className="text-red-700">
                  <span><span key={tUi("type")}>{tUi("type")}</span></span> <span className="font-mono font-bold">{agent?.name}</span> <span><span key={tUi("toConfirm")}>{tUi("toConfirm")}</span></span>
                </Label>
                <Input
                  id="confirm-delete"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={tUi("typeAgentNameHere")}
                  className="mt-1"
                />
              </div>

              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting || confirmText !== agent?.name}
                className="w-full"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span key={tUi("deleting")}>{tUi("deleting")}</span></>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    <span key={tUi("deleteAgent")}>{tUi("deleteAgent")}</span></>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
